import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { finalize, Observable, startWith, Subject, switchMap } from 'rxjs';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PHONE_REGEX } from '@app/shared/util/validators';
import { Select } from '@ngxs/store';
import { AppState } from '@app/shared/app.state';
import { ModelPlainMapOf } from '@technocom/common/data';
import { Citizenship } from '@app/shared/models/dictionaries/citizenship';
import { Purpose } from '@app/shared/models/dictionaries/purpose';
import { Residence } from '@app/shared/models/dictionaries/residence';
import { GetCitizenship, GetPurpose, GetResidence } from '@app/shared/app.action';
import { bookIcon, fileAddIcon, userIcon } from '@progress/kendo-svg-icons';
import { HttpService } from '@technocom/common/http';
import { Endpoints } from '@app/shared/util/endpoints';
import { countdownFrom } from '@app/shared/util/helpers';
import { EntityProvider } from '@technocom/data';
import { PersonalUser } from '@app/shared/models/user/personal-user';
import { HttpHeaders } from '@angular/common/http';
import { RouterService } from '@technocom/core';
import { DEFAULT_ERRORS } from '@app/shared/constants/form-errors';
import { AuthenticationManagerService } from '@technocom/auth';
import { debounceTime, tap } from 'rxjs/operators';
import { DialogService } from '@technocom/ui/dialogs';
import { ToastrService } from 'ngx-toastr';
import { FILTER_SETTINGS } from '@app/shared/constants/filter-settings';

@Component({
  selector: 'app-registration-page',
  templateUrl: './registration-page.component.html',
  styleUrls: ['./registration-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistrationPageComponent implements OnInit {
  form: UntypedFormGroup;
  defaultErrors = DEFAULT_ERRORS;
  filterSettings = FILTER_SETTINGS;
  formCodeShowed = false;
  fetching: boolean;
  countResend;
  @ViewChild('passwordbox') public passwordbox: TextBoxComponent;
  @ViewChild('cpasswordbox') public cpasswordbox: TextBoxComponent;
  @Select(AppState.getCitizenships) citizenships$: Observable<ModelPlainMapOf<Citizenship>[]>;
  @Select(AppState.getPurpose) purpose$: Observable<ModelPlainMapOf<Purpose>[]>;
  @Select(AppState.getResidence) residence$: Observable<ModelPlainMapOf<Residence>[]>;

  actions = { GetCitizenship, GetPurpose, GetResidence };

  validStepCallback = (index: number): boolean => {
    return this.isValidStep(index);
  };

  private passEqual() {
    return (group: FormGroup) => {
      return !group.dirty || !group.touched || group.get('password').value === group.get('confirmPassword').value
        ? null
        : { custom: 'Пароли не совпадают' };
    };
  }

  public current = 0;
  public steps = [
    { label: 'Личная информация', svgIcon: userIcon, isValid: this.validStepCallback },
    { label: 'Проверка номера телефона', svgIcon: bookIcon, isValid: this.validStepCallback },
    { label: 'Регистрация', svgIcon: fileAddIcon, disabled: true },
  ];

  readonly resend$ = new Subject<void>();
  countdown$;

  ngOnInit() {
    this.form = this.generateForm();
  }

  get localizeBasePath(): string {
    return 'profile.registration';
  }

  get isGetCodeBtnDisabled(): boolean {
    return this.form.get('login').invalid;
  }

  get isSendBtnDisabled(): boolean {
    return (
      this.form.get('code').invalid ||
      this.form.invalid ||
      this.form.get('password').value !== this.form.get('confirmPassword').value
    );
  }
  protected httpService = inject(HttpService);
  protected fb = inject(UntypedFormBuilder);
  protected changeDetector = inject(ChangeDetectorRef);
  protected entityProvider = inject(EntityProvider);
  protected router = inject(RouterService);
  protected dialogService = inject(DialogService);
  protected authService = inject(AuthenticationManagerService);
  protected toastr = inject(ToastrService);

  getCheckCode(): void {
    if (this.isGetCodeBtnDisabled) {
      return;
    }

    this.countResend = 30;
    this.countdown$ = this.resend$.pipe(
      startWith(0),
      switchMap(() => countdownFrom(this.countResend).pipe(tap(num => (this.countResend = num)))),
    );

    const params = { login: this.form.get('login').value };
    this.httpService
      .tryPost({ url: Endpoints.ofRegistration('check-registration'), params })
      .pipe(
        switchMap(res => {
          return this.httpService
            .tryPost({ url: Endpoints.ofRegistration('send-code'), params })
            .pipe(switchMap(() => this.countdown$));
        }),
      )
      .subscribe(() => {
        this.formCodeShowed = true;
        this.steps[2].disabled = false;
        this.changeDetector?.markForCheck();
      });
  }

  // смена видимости ввода пароля
  public toggleVisibility(passwordbox): void {
    const inputEl = passwordbox.input.nativeElement;

    if (inputEl.type === 'password') {
      inputEl.type = 'text';
    } else {
      inputEl.type = 'password';
    }
  }

  registration(): void {
    if (this.isSendBtnDisabled || this.fetching) {
      return;
    }

    this.fetching = true;

    const model = this.entityProvider.getFactory(PersonalUser).create(this.form.value);
    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/json'),
    };

    this.dialogService
      .wait(
        this.httpService.tryPost(Endpoints.ofRegistration(), model, options).pipe(
          debounceTime(1000),
          switchMap(() => this.router.open('profile/personal-data', true)),
          finalize(() => (this.fetching = false)),
        ),
      )
      .subscribe(() => {
        this.toastr.success('Пользователь зарегистрирован');
      });
  }

  goToLoginPage() {
    void this.router.goTo('login/local', { replaceUrl: true });
  }

  private generateForm() {
    const form = this.fb.group(
      {
        login: [null, [Validators.pattern(PHONE_REGEX), Validators.required]],
        code: [null, [Validators.required, Validators.pattern('[0-9]{4}')]],
        password: [null, [Validators.required, Validators.minLength(8)]],
        confirmPassword: [null, [Validators.required, Validators.minLength(8)]],
        profileInfo: this.fb.group({
          citizenship: [null, [Validators.required]],
          birthYear: [null, [Validators.required, Validators.pattern('[1-2]{1}[0-9]{3}')]],
        }),
      },
      { validators: this.passEqual() },
    );

    return form;
  }

  nextStep(): void {
    this.current++;
    this.onStepChange(this.current);
  }

  getErrorOfForm(errorCode: string): any {
    return this.form.getError(errorCode);
  }

  getErrorOfField(fieldName: string, errorCode: string): any {
    return this.form.get(fieldName).getError(errorCode);
  }

  isValidStep(step): boolean {
    switch (step) {
      case 0:
        return this.form?.get('profileInfo').valid;
      case 1:
        return this.form?.get('login').valid && this.form?.get('code').valid;
      default:
        return false;
    }
  }

  onStepChange(stepIndex: number): void {
    if (stepIndex === 2) {
      setTimeout(() => {
        this.passwordbox.input.nativeElement.type = 'password';
        this.cpasswordbox.input.nativeElement.type = 'password';
      }, 100);
    }
  }
}
